import { Button, createStyles, Divider, Grid, InputAdornment, makeStyles, TextField, Theme, Typography } from "@material-ui/core";
import { MouseEventHandler, useState } from "react";
import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { IRegistrationCredentialsFormValues } from "../_interfaces/IRegistrationCredentialsFormValues";
import { Alert } from "@material-ui/lab";
import TermsOfServiceAcceptance from "../../../components/Authentication/RegistrationSteps/Shared/TermsOfServiceAcceptance";
import FormLoadingSpinner from "../../../components/Organization/VolunteerOpportunities/FormLoadingSpinner";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        form: {
            position: 'relative'
        },
        textField: {
            margin: theme.spacing(1),
            width: '40ch',
            flexGrow: 1,
            [theme.breakpoints.down('xs')]: {
                width: 'auto'
            },
            '& > .MuiFormHelperText-root': {
                whiteSpace: 'pre-line'
            }
        },
        textFieldWrapper: {
            display: 'flex',
            flexGrow: 1
        },
        errorIcon: {
            color: theme.palette.accent.main
        },
        matchIcon: {
            color: theme.palette.primary.main
        },
        title: {
            color: theme.palette.action.active,
            marginBottom: theme.spacing(3),
            textAlign: 'center'
        },
        linkWrapper: {
            marginTop: theme.spacing(2),
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
            color: theme.palette.text.secondary,
            textAlign: 'center'
        },
        divider: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
        signUpButton: {
            marginTop: theme.spacing(1),
            color: theme.palette.common.white,
            background: theme.palette.success.main,
            '&:hover': {
                background: theme.palette.success.main,
            }
        },
        loginButton: {
            marginTop: theme.spacing(1),
            color: theme.palette.common.white,
        },
        alert: {
            margin: theme.spacing(1),
            flexGrow: 1,
            textAlign: 'left'
        },
        termsOfService: {
            color: theme.palette.text.secondary,
        }
    }),
);

interface RegistrationCredentialsFormProps {
    values: IRegistrationCredentialsFormValues;
    onChange: (patch: Partial<IRegistrationCredentialsFormValues>) => void;
    onSubmit: (overrideValues?: Partial<IRegistrationCredentialsFormValues>) => void;
    isSubmitting?: boolean;
    error?: string;
    fieldErrors?: Partial<Record<keyof IRegistrationCredentialsFormValues, string>>;
}

export const RegistrationCredentialsForm: React.FC<RegistrationCredentialsFormProps> = ({
    values,
    onChange,
    onSubmit,
    isSubmitting,
    error,
    fieldErrors
}) => {

    /********** State **********/

    const getFormId = () => {
        return `form-sign-up-${Date.now()}-${Math.random()}`;
    }

    const [formId] = useState(() => getFormId());

    /********** React hooks **********/

    const classes = useStyles();

    /********** Event handlers **********/

    const onSignUpClicked = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault(); // Prevent page reload
        event.stopPropagation();
        onSubmit();
    }

    const onLoginClicked: MouseEventHandler<HTMLButtonElement> = (event) => {
        event.preventDefault(); // Prevent page reload
        event.stopPropagation();
        onChange({ hasAccount: true })
        onSubmit({ hasAccount: true });
    }

    const emailError = fieldErrors?.username;
    const passwordError = fieldErrors?.password;
    const confirmedPasswordError = fieldErrors?.confirmedPassword;
    const termsOfServiceError = fieldErrors?.termsOfServiceAccepted;

    /********** Render **********/

    return (
        <form onSubmit={onSignUpClicked} noValidate autoComplete="off" id={formId} className={classes.form}>
            <div className={classes.title}>
                <Typography variant="h5">Create your account.</Typography>
            </div>
            {values.loading ? <FormLoadingSpinner /> : undefined}
            {error && <Alert severity="error" className={classes.alert}>{error}</Alert>}
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <div className={classes.textFieldWrapper}>
                        <TextField
                            id="standard-email-input"
                            label="Email"
                            autoComplete="current-email"
                            variant="outlined"
                            required
                            value={values.username}
                            onChange={(e) => onChange({ username: e.target.value })}
                            autoFocus
                            className={classes.textField}
                            error={emailError !== undefined && emailError.length > 0}
                            helperText={emailError && emailError.length > 0 ? emailError : undefined}
                            {...values.fieldProperties?.username}
                        />
                    </div>
                    <div className={classes.textFieldWrapper}>
                        <TextField
                            id="standard-password-input"
                            label="Password"
                            type="password"
                            autoComplete="current-password"
                            variant="outlined"
                            required
                            value={values.password}
                            onChange={(e) => onChange({ password: e.target.value })}
                            className={classes.textField}
                            error={passwordError !== undefined && passwordError.length > 0}
                            helperText={passwordError && passwordError.length > 0 ? passwordError : undefined}
                            {...values.fieldProperties?.password}
                        />
                    </div>
                    <div className={classes.textFieldWrapper}>
                        <TextField
                            id="confim-password-input"
                            label="Confirm Password"
                            type="password"
                            autoComplete="current-password"
                            variant="outlined"
                            required
                            value={values.confirmedPassword}
                            onChange={(e) => onChange({ confirmedPassword: e.target.value })}
                            className={classes.textField}
                            error={confirmedPasswordError !== undefined && confirmedPasswordError.length > 0}
                            helperText={confirmedPasswordError && confirmedPasswordError.length > 0 ? confirmedPasswordError : undefined}
                            InputProps={{
                                endAdornment: values.confirmedPassword.length > 0
                                    ? <InputAdornment position="end">
                                        {values.passwordsMatch
                                            ? <CheckCircleIcon className={classes.matchIcon} />
                                            : <ErrorIcon className={classes.errorIcon} />
                                        }
                                    </InputAdornment>
                                    : null
                            }}
                            {...values.fieldProperties?.confirmedPassword}
                        />
                    </div>
                    <div className={classes.termsOfService}>
                        <TermsOfServiceAcceptance
                            checked={values.termsOfServiceAccepted}
                            onChange={(accepted) => onChange({ termsOfServiceAccepted: accepted })}
                            error={termsOfServiceError && termsOfServiceError.length > 0 ? termsOfServiceError : undefined}
                            organizationTerms
                            disabled={isSubmitting}
                        />
                    </div>
                    <Button
                        color="inherit"
                        variant="contained"
                        fullWidth
                        className={classes.signUpButton}
                        size="large"
                        onClick={() => onChange({ hasAccount: false })}
                        type="submit"
                        form={formId}
                        disabled={isSubmitting}
                    >
                        Sign up
                    </Button>
                    <Divider className={classes.divider} />
                    <div className={classes.linkWrapper}>
                        <Typography variant="body2">
                            {`Already have an account?`}
                        </Typography>
                        <Button
                            color="primary"
                            variant="contained"
                            fullWidth
                            className={classes.loginButton}
                            size="large"
                            onClick={onLoginClicked}
                            disabled={isSubmitting}
                        >
                            Login
                        </Button>
                    </div>
                </Grid>
            </Grid>
        </form>
    );
};
